
import respUtility from '~/mixins/respUtility';
import { debounce } from '~/mixins/helpers';
import {
  GET_PRODUCT_GALLERY_DATA_PLP,
} from '~/api/apolloProductQueries';
import { Portal, PortalTarget } from 'portal-vue';

export default {
  name: 'ListingProductsList',
  mixins: [respUtility],
  props: {
    items: {
      type: Array,
      default() { return []; },
    },
    itemsCount: {
      type: Number,
      default: 0,
    },
    attrs: {
      type: Object,
      default() {
        return {};
      },
    },
    perPage: {
      type: Number,
      default: 36,
    },
    searchParams: {
      type: Object,
      default() { return {}; },
    },
    loading: {
      type: Boolean,
      default: false,
    },
    additionalData: {
      type: Object,
    },
    isSearchParams: {
      type: Boolean,
      default: false,
    },
    desktopFiltersVisible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      mountedChildCount: 0,
      hoverIndex: null,
      slugProduct: {},
      abortController: null,
      hoverItemDone1: false,
      hoverItemDone2: false,
      timerId: null,
    };
  },
  watch: {
    loading(val) {
      if (val) {
        this.resetAll();
      }
    },
  },
  async mounted() {
    this.validatePlpLocalStorage();
  },
  computed: {
    hasBannerData() {
      return this.$device.isMobile
        ? !!this.additionalData?.bannerData?.mobile?.bannerImage
        : !!this.additionalData?.bannerData.desktop?.bannerImage;
    },
    banner() {
      if (!this.hasBannerData) {
        return null;
      }
      return this.$device.isMobile
        ? this.additionalData.bannerData.mobile
        : this.additionalData.bannerData.desktop;
    },
    showBanner() {
      return !this.isSearchParams && !this.additionalData?.isVirtual && this.hasBannerData;
    },
  },
  methods: {
    onChildMounted() {
      this.mountedChildCount += 1;
      if (this.mountedChildCount === this.itemsCount) {
        this.resetMountedChildCount();
        if (window.productEventTracking) {
          this.setInteractionEvents();
        }
      }
    },
    resetMountedChildCount() {
      this.mountedChildCount = 0;
    },
    validatePlpLocalStorage() {
      const allIds = JSON.parse(localStorage.getItem('triggerEventsClicks'));

      if (!allIds) {
        return;
      }

      const currentDate = new Date();
      const filteredIds = allIds.filter((e) => new Date(e.expiry) >= currentDate);

      if (filteredIds.length !== allIds.length) {
        localStorage.setItem('triggerEventsClicks', JSON.stringify(filteredIds), { path: '/' });
      }
    },
    productListingItemCardClick(sku) {
      let allIds = JSON.parse(localStorage.getItem('triggerEventsClicks'));
      if (!allIds) {
        allIds = [];
      }
      if (allIds.filter((e) => e.id === sku && e.type === 1).length > 0) {
        return;
      }
      allIds = allIds.filter((e) => e.id !== sku);
      const exdate = new Date();
      exdate.setDate(exdate.getDate() + 30);
      const newId = {
        id: sku,
        mainProductId: 0,
        type: 5,
        expiry: exdate.toISOString(),
      };
      allIds.push(newId);
      localStorage.setItem('triggerEventsClicks', JSON.stringify(allIds), { path: '/' });
    },
    triggerEventsImpressions(eventProducts) {
      const allIds = JSON.parse(sessionStorage.getItem('triggerEventsImpressions')) || [];
      eventProducts.forEach((eventProduct) => {
        const newId = {
          productId: eventProduct.product_id_i,
          mainProductId: eventProduct.main_product_i,
          interaction: eventProduct.interaction_type_s,
          type: eventProduct.type_i,
        };
        const exists = allIds.some(
          (e) => e.productId === newId.productId && e.mainProductId === newId.mainProductId && e.type === newId.type,
        );
        if (!exists) {
          allIds.push(newId);
        }
      });
      if (allIds.length > 0) {
        sessionStorage.setItem('triggerEventsImpressions', JSON.stringify(allIds));
      }
      return allIds;
    },
    setInteractionEvents() {
      let scrollTimeout;
      let eventProducts = [];
      const listingItem = [];
      this.items.forEach((item) => {
        listingItem[item.id] = item.price.finalPrice;
        if (item.items) {
          item.items.forEach((groupItem) => {
            listingItem[groupItem.id] = groupItem.price.finalPrice;
          });
        }
      });
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const productId = entry.target.getAttribute('data-item-id');
          if (entry.isIntersecting && productId) {
            eventProducts.push({
              main_product_i: 0,
              product_id_i: parseInt(productId, 10),
              type_i: 5,
              interaction_type_s: 'impression',
              price_f: parseFloat(listingItem[productId]),
            });
            observer.unobserve(entry.target);
          }
        });
        const triggerEvents = JSON.parse(sessionStorage.getItem('triggerEventsImpressions')) || [];
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          const filteredEventProducts = eventProducts.filter(
            (eventProduct) => !triggerEvents.some(
              (triggerEvent) => triggerEvent.mainProductId === eventProduct.main_product_i
                && triggerEvent.productId === eventProduct.product_id_i
                && triggerEvent.type === eventProduct.type_i,
            ),
          );
          if (filteredEventProducts.length > 0) {
            window.productEventTracking.dispatchCustomEvent('track-product-event', filteredEventProducts);
            this.triggerEventsImpressions(filteredEventProducts);
            eventProducts = [];
          }
        }, 200);
      }, { threshold: 0.5 });
      this.$nextTick(() => {
        const triggeredClicks = JSON.parse(localStorage.getItem('triggerEventsClicks')) || [];
        this.$refs.itemCard.forEach((comp) => {
          observer.observe(comp.$el);
          if (!triggeredClicks.some(
            (item) => item.id === comp.$el.getAttribute('data-ext-sku')
            && item.type === 5
            && item.mainProductId === 0,
          )) {
            const handleClick = (event) => this.handleClick(event, comp.$el, listingItem, handleClick);
            comp.$el.addEventListener('click', handleClick);
          }
        });
      });
    },
    handleClick(event, item, listingItem, callback) {
      if (window.productEventTracking) {
        this.$taskManager.append(() => {
          const productId = item.getAttribute('data-item-id');
          if (productId && this.isClickable(event.target, item)) {
            const data = {
              main_product_i: 0,
              product_id_i: parseInt(productId, 10),
              type_i: 5,
              interaction_type_s: 'click',
              price_f: parseFloat(listingItem[productId]),
            };
            window.productEventTracking.dispatchCustomEvent('track-product-event', data);
            this.productListingItemCardClick(item.getAttribute('data-ext-sku'));
            item.removeEventListener('click', callback);
          }
        });
      }
    },
    isClickable(element, container, onlylink = false) {
      let isClickable = false;
      let current = element;
      while (current && current !== container) {
        if ((!onlylink && current.tagName === 'BUTTON') || current.tagName === 'A') {
          isClickable = true;
          break;
        }
        current = current.parentElement;
      }
      return isClickable;
    },
    async showSlider(index) {
      if (this.$isSpider || (this.$device.isMobileOrTablet || this.isTouchDevice)) {
        return;
      }

      await this.setIndexProduct(index);

      const $slug = await Object.keys(this.slugProduct)?.length && this.slugProduct[this.hoverIndex]?.slug
        ? this.slugProduct[this.hoverIndex].slug
        : this.items[index].slug;

      if (
        this.slugProduct[this.hoverIndex]?.slug
        && $slug === this.slugProduct[this.hoverIndex].slug
        && this.slugProduct[this.hoverIndex]?.gallery?.length
      ) {
        this.getSliderFromSlugProduct();
        return;
      }

      await this.setSlugProduct($slug);
      await this.setAbortController();
      await this.getSliderRequest();
    },
    getSliderFromSlugProduct() {
      if (this.timerId) {
        clearTimeout(this.timerId);
        this.timerId = null;
        this.hoverItemDone2 = false;
      }
      this.hoverItemDone1 = true;
      this.timerId = setTimeout(() => {
        this.hoverItemDone2 = true;
      }, 700);
    },
    setIndexProduct(index) {
      this.hoverIndex = index;
      if (!this.slugProduct[this.hoverIndex]) {
        this.slugProduct[this.hoverIndex] = {};
      }
    },
    setSlugProduct(slug) {
      const $slug = slug[0] === '/' ? slug.slice(1) : slug;
      this.slugProduct[this.hoverIndex].slug = $slug;
      this.slugProduct = { ...this.slugProduct };
    },
    setAbortController() {
      this.abortController = new AbortController();
    },
    getSliderRequest: debounce(async function showSliderRequestDebounce() {
      if (!this.abortController) {
        return;
      }

      if (this.timerId) {
        clearTimeout(this.timerId);
        this.timerId = null;
        this.hoverItemDone2 = false;
      }

      const defaultParams = {
        slug: this.slugProduct[this.hoverIndex]?.slug,
        type: 'PLP',
      };

      try {
        const { products: { productBySlug } } = await this.$graphql.default
          .request({
            document: GET_PRODUCT_GALLERY_DATA_PLP,
            signal: this.abortController?.signal,
            variables: defaultParams,
          }) || {};
        const gallery = productBySlug?.gallery?.productListImages || {};
        this.slugProduct[this.hoverIndex].gallery = gallery;
        this.slugProduct = { ...this.slugProduct };
      } catch (e) {
        if (e !== 'AbortSignal') {
          console.error(e);
        }
      } finally {
        if (this.slugProduct[this.hoverIndex]?.gallery?.length) {
          this.hoverItemDone1 = true;
          this.timerId = setTimeout(() => {
            this.hoverItemDone2 = true;
          }, 700);
        }
      }
    }, 300),
    hideSlider() {
      if (this.$isSpider || (this.$device.isMobileOrTablet || this.isTouchDevice)) {
        return;
      }

      if (this.timerId) {
        clearTimeout(this.timerId);
        this.timerId = null;
      }

      this.hoverItemDone1 = false;
      this.hoverItemDone2 = false;
      this.abortRequest();
    },
    abortRequest() {
      if (!this.abortController) {
        return;
      }

      this.abortController.abort('AbortSignal');
      this.abortController = null;
    },
    async updateRequestForDynamics(slug) {
      if (this.$isSpider || this.$device.isMobileOrTablet || this.isTouchDevice) {
        return;
      }

      const $slug = slug[0] === '/' ? slug.slice(1) : slug;

      if (this.slugProduct[this.hoverIndex]?.slug && $slug === this.slugProduct[this.hoverIndex].slug) {
        this.getSliderFromSlugProduct();
        return;
      }

      if (this.slugProduct[this.hoverIndex]?.gallery) {
        const { gallery, ...rest } = this.slugProduct[this.hoverIndex];
        this.slugProduct[this.hoverIndex] = rest;
        this.slugProduct = { ...this.slugProduct };
      }

      await this.setSlugProduct(slug);
      await this.setAbortController();
      await this.getSliderRequest();
    },
    resetAll() {
      this.hoverIndex = null;
      this.slugProduct = {};
      this.abortController = null;
      this.hoverItemDone1 = false;
      this.hoverItemDone2 = false;
      if (this.timerId) {
        clearTimeout(this.timerId);
        this.timerId = null;
      }
    },
  },
  components: {
    Portal,
    PortalTarget,
  },
};
